/**
 * ModoAutomaticoEP01v3 — "Modo Automático" EP 01
 * Talking-head A-roll + 12 overlays positioned in bottom 35% safe zone
 * 73.1s @ 60fps = 4386 frames
 * IDV palette: primary #1E3A61 / secondary #00DF82 / third #FECB18
 */
import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  spring,
  interpolate,
  Sequence,
  Video,
  staticFile,
} from "remotion";
import {SPRINGS} from "../presets/editing-styles";
import {CLAMP, IDV, FONT_STACK} from "../presets/idv";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {Vignette} from "../components/dimus/Vignette";
import {LowerThird} from "../components/text/LowerThird";

// ─── Constants ───────────────────────────────────────────────────────────────
const FPS = 30;
export const MODO_AUTO_EP01_V3_TOTAL = Math.round(73.1 * FPS); // 4386

// ─── Fonts ───────────────────────────────────────────────────────────────────
const F = {
  bebas: '"Bebas Neue", "Impact", "Arial Black", sans-serif',
  inter: `"Inter", ${FONT_STACK}`,
  mono: '"JetBrains Mono", "Fira Code", monospace',
};

// ─── Spring helpers ───────────────────────────────────────────────────────────
function spr(
  frame: number,
  fps: number,
  cfg: Record<string, number | boolean> = SPRINGS.snappy,
  offset = 0
) {
  return spring({frame: frame - offset, fps, config: cfg});
}

function lerp(
  v: number,
  range: readonly number[],
  output: readonly number[]
) {
  return interpolate(v, range, output, CLAMP);
}

// ─── STAMP DISCLOSURE — permanent bottom-left ─────────────────────────────────
const StampDisclosure: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = lerp(frame, [0, 30], [0, 0.6]);
  return (
    <div
      style={{
        position: "absolute",
        bottom: 36,
        left: 36,
        opacity,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(8px)",
        borderRadius: 4,
        padding: "5px 10px",
        fontFamily: F.inter,
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: "0.1em",
        color: IDV.white,
        textTransform: "uppercase",
        border: `1px solid rgba(255,255,255,0.1)`,
        pointerEvents: "none",
      }}
    >
      100% EDITADO POR AI
    </div>
  );
};

// ─── OV-01: "3:00h" red timer stat (7–10s) ───────────────────────────────────
const OV01_Timer: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const s = spr(frame, fps, SPRINGS.bouncy);
  const scale = lerp(s, [0, 1], [0.4, 1]);
  const opacity = lerp(s, [0, 1], [0, 1]);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 400,
        left: "50%",
        transform: `translateX(-50%) scale(${scale})`,
        opacity,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div
        style={{
          background: "rgba(0,0,0,0.75)",
          border: `3px solid ${IDV.error}`,
          borderRadius: 16,
          padding: "20px 48px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
        }}
      >
        <div
          style={{
            fontFamily: F.bebas,
            fontSize: 120,
            color: IDV.error,
            lineHeight: 1,
            letterSpacing: "0.02em",
          }}
        >
          3:00h
        </div>
        <div
          style={{
            fontFamily: F.inter,
            fontSize: 28,
            fontWeight: 600,
            color: "rgba(248,113,113,0.8)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
          }}
        >
          POR POST
        </div>
      </div>
    </div>
  );
};

// ─── OV-02: Feed pobre — 4 gray squares with X (13–15.5s) ───────────────────
const OV02_FeedPobre: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;

  return (
    <div
      style={{
        position: "absolute",
        right: 60,
        bottom: 460,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 90px)",
          gap: 8,
        }}
      >
        {[0, 1, 2, 3].map((i) => {
          const s = spr(frame, fps, SPRINGS.snappy, i * 4);
          const tx = lerp(s, [0, 1], [80, 0]);
          const op = lerp(s, [0, 1], [0, 0.9]);
          return (
            <div
              key={i}
              style={{
                transform: `translateX(${tx}px)`,
                opacity: op,
                width: 90,
                height: 90,
                background: "rgba(60,60,60,0.9)",
                borderRadius: 8,
                border: "2px solid rgba(255,255,255,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 32,
                color: "rgba(255,100,100,0.8)",
                fontWeight: 900,
              }}
            >
              ✕
            </div>
          );
        })}
      </div>
      <div
        style={{
          fontFamily: F.inter,
          fontSize: 28,
          fontWeight: 700,
          color: "rgba(255,255,255,0.7)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          opacity: lerp(spr(frame, fps, SPRINGS.snappy, 8), [0, 1], [0, 1]),
        }}
      >
        FEED POBRE
      </div>
    </div>
  );
};

// ─── OV-03: "3 estilos" pills left side (17–20s) ────────────────────────────
const OV03_EstilosPills: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const pills = [
    {label: "PESSOA 1", color: IDV.gray},
    {label: "PESSOA 2", color: IDV.primary},
    {label: "PESSOA 3", color: IDV.secondary},
  ];

  return (
    <div
      style={{
        position: "absolute",
        left: 48,
        bottom: 480,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {pills.map((pill, i) => {
        const s = spr(frame, fps, SPRINGS.snappy, i * 6);
        const tx = lerp(s, [0, 1], [-120, 0]);
        const op = lerp(s, [0, 1], [0, 1]);
        return (
          <div
            key={i}
            style={{
              transform: `translateX(${tx}px)`,
              opacity: op,
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "rgba(0,0,0,0.75)",
              border: `2px solid ${pill.color}`,
              borderRadius: 40,
              padding: "10px 24px",
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: pill.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: F.inter,
                fontSize: 26,
                fontWeight: 700,
                color: IDV.white,
                letterSpacing: "0.06em",
              }}
            >
              {pill.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── OV-04: FLARE hero card — center below face (24–29s) ────────────────────
const OV04_FlareCard: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const s = spr(frame, fps, SPRINGS.heavy);
  const ty = lerp(s, [0, 1], [-80, 0]);
  const op = lerp(s, [0, 1], [0, 1]);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 520,
        left: "50%",
        transform: `translateX(-50%) translateY(${ty}px)`,
        opacity: op,
        width: 640,
      }}
    >
      <div
        style={{
          background: IDV.bg,
          border: `3px solid ${IDV.secondary}`,
          borderRadius: 20,
          padding: "32px 48px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          boxShadow: `0 0 40px rgba(0,223,130,0.25)`,
        }}
      >
        <div
          style={{
            fontFamily: F.bebas,
            fontSize: 80,
            color: IDV.white,
            letterSpacing: "0.06em",
            lineHeight: 1,
          }}
        >
          FLARE
        </div>
        <div
          style={{
            fontFamily: F.inter,
            fontSize: 24,
            fontWeight: 600,
            color: IDV.secondary,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          CMO AI
        </div>
      </div>
    </div>
  );
};

// ─── OV-05: 3 teams pills bottom strip (30–35s) ──────────────────────────────
const OV05_TeamsStrip: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const teams = [
    {label: "DESIGN", color: IDV.secondary},
    {label: "VÍDEO", color: IDV.third},
    {label: "COPY", color: IDV.primary},
  ];

  return (
    <div
      style={{
        position: "absolute",
        bottom: 380,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        flexDirection: "row",
        gap: 20,
      }}
    >
      {teams.map((team, i) => {
        const s = spr(frame, fps, SPRINGS.bouncy, i * 5);
        const scale = lerp(s, [0, 1], [0.4, 1]);
        const op = lerp(s, [0, 1], [0, 1]);
        return (
          <div
            key={i}
            style={{
              transform: `scale(${scale})`,
              opacity: op,
              background: "rgba(0,0,0,0.8)",
              border: `2px solid ${team.color}`,
              borderRadius: 40,
              padding: "14px 32px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: team.color,
              }}
            />
            <span
              style={{
                fontFamily: F.inter,
                fontSize: 28,
                fontWeight: 800,
                color: team.color,
                letterSpacing: "0.1em",
              }}
            >
              {team.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── OV-07: FLARE arbita stamp top-right corner (37–44s) ────────────────────
const OV07_FlareStamp: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const s = spr(frame, fps, SPRINGS.bouncy);
  const scale = lerp(s, [0, 1], [0, 1]);
  const op = lerp(s, [0, 1], [0, 1]);
  // subtle pulse after entry
  const pulse =
    frame > 30
      ? 1 + 0.04 * Math.sin(((frame - 30) / fps) * 2 * Math.PI * 1.5)
      : 1;

  return (
    <div
      style={{
        position: "absolute",
        top: 200,
        right: 60,
        transform: `scale(${scale * pulse})`,
        opacity: op,
        width: 120,
        height: 120,
        borderRadius: "50%",
        background: IDV.bg,
        border: `4px solid ${IDV.secondary}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        boxShadow: `0 0 24px rgba(0,223,130,0.4)`,
      }}
    >
      <div
        style={{
          fontFamily: F.inter,
          fontSize: 18,
          fontWeight: 900,
          color: IDV.secondary,
          letterSpacing: "0.06em",
        }}
      >
        FLARE
      </div>
      <div style={{fontSize: 22, color: IDV.secondary, lineHeight: 1}}>✓</div>
    </div>
  );
};

// ─── OV-08: 3h → 20min hero stat (47–52s) ───────────────────────────────────
const OV08_StatHero: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;

  const sLeft = spr(frame, fps, SPRINGS.snappy);
  const sRight = spr(frame, fps, SPRINGS.snappy);
  const txLeft = lerp(sLeft, [0, 1], [-200, 0]);
  const txRight = lerp(sRight, [0, 1], [200, 0]);
  const op = lerp(sLeft, [0, 1], [0, 1]);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 420,
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 0,
        opacity: op,
        padding: "0 40px",
      }}
    >
      {/* Left: 3h crossed out */}
      <div
        style={{
          transform: `translateX(${txLeft}px)`,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          position: "relative",
        }}
      >
        <div
          style={{
            fontFamily: F.bebas,
            fontSize: 110,
            color: IDV.error,
            lineHeight: 1,
            letterSpacing: "0.04em",
            position: "relative",
          }}
        >
          3h
          {/* Strikethrough */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "-5%",
              width: "110%",
              height: 6,
              background: IDV.error,
              borderRadius: 3,
              transform: "rotate(-8deg)",
            }}
          />
        </div>
        <div
          style={{
            fontFamily: F.inter,
            fontSize: 22,
            fontWeight: 600,
            color: "rgba(248,113,113,0.7)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          antes
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          width: 3,
          height: 140,
          background: "rgba(255,255,255,0.2)",
          borderRadius: 2,
          flexShrink: 0,
        }}
      />

      {/* Right: 20min in green */}
      <div
        style={{
          transform: `translateX(${txRight}px)`,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
        }}
      >
        <div
          style={{
            fontFamily: F.bebas,
            fontSize: 110,
            color: IDV.secondary,
            lineHeight: 1,
            letterSpacing: "0.04em",
            textShadow: `0 0 30px rgba(0,223,130,0.6)`,
          }}
        >
          20min
        </div>
        <div
          style={{
            fontFamily: F.inter,
            fontSize: 22,
            fontWeight: 600,
            color: IDV.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          hoje
        </div>
      </div>
    </div>
  );
};

// ─── OV-09: "MESMA MARCA" badge (52–56s) ─────────────────────────────────────
const OV09_MesmaMarca: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const s = spr(frame, fps, SPRINGS.bouncy);
  const scale = lerp(s, [0, 1], [0.3, 1]);
  const op = lerp(s, [0, 1], [0, 1]);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 420,
        left: "50%",
        transform: `translateX(-50%) scale(${scale})`,
        opacity: op,
      }}
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          border: `3px solid ${IDV.secondary}`,
          borderRadius: 60,
          padding: "18px 48px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          boxShadow: `0 0 30px rgba(0,223,130,0.3)`,
        }}
      >
        <span style={{fontSize: 40, color: IDV.secondary}}>✓</span>
        <span
          style={{
            fontFamily: F.inter,
            fontSize: 46,
            fontWeight: 900,
            color: IDV.white,
            letterSpacing: "0.04em",
          }}
        >
          MESMA MARCA
        </span>
      </div>
    </div>
  );
};

// ─── OV-10: "100% AI" ribbon full-width (57–62s) ─────────────────────────────
const OV10_AiRibbon: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const s = spr(frame, fps, SPRINGS.snappy);
  const tx = lerp(s, [0, 1], [1080, 0]);
  const op = lerp(s, [0, 1], [0, 1]);
  // shimmer effect
  const shimmer = lerp(
    ((frame * 4) % 1080),
    [0, 540, 1080],
    [0, 1, 0]
  );

  return (
    <div
      style={{
        position: "absolute",
        bottom: 430,
        left: 0,
        right: 0,
        transform: `translateX(${tx}px)`,
        opacity: op,
        height: 80,
        background: "rgba(10,10,10,0.88)",
        borderTop: `2px solid rgba(255,255,255,0.15)`,
        borderBottom: `2px solid rgba(255,255,255,0.15)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Shimmer sweep */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: `${shimmer * 100 - 20}%`,
          width: "20%",
          height: "100%",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
          pointerEvents: "none",
        }}
      />
      {/* Scanline */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 4px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          fontFamily: F.inter,
          fontSize: 30,
          fontWeight: 900,
          color: IDV.white,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          position: "relative",
        }}
      >
        100% EDITADO POR AI — ROTEIRO · CORTE · VOZ · LEGENDA
      </div>
    </div>
  );
};

// ─── OV-11: TIME CRIATIVO pulse CTA (62–67s) ─────────────────────────────────
const OV11_CTAPulse: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const s = spr(frame, fps, SPRINGS.bouncy);
  const scale = lerp(s, [0, 1], [0.5, 1]);
  const op = lerp(s, [0, 1], [0, 1]);
  // oscillate after entry (frame > 20)
  const osc =
    frame > 20
      ? 1 + 0.05 * Math.sin(((frame - 20) / fps) * 2 * Math.PI * 1.2)
      : 1;

  return (
    <div
      style={{
        position: "absolute",
        bottom: 340,
        left: "50%",
        transform: `translateX(-50%) scale(${scale * osc})`,
        opacity: op,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          fontFamily: F.inter,
          fontSize: 64,
          fontWeight: 900,
          color: IDV.secondary,
          letterSpacing: "0.02em",
          textAlign: "center",
          textShadow: `0 0 40px rgba(0,223,130,0.5)`,
          lineHeight: 1.1,
        }}
      >
        ⬇ TIME CRIATIVO
      </div>
      <div
        style={{
          fontFamily: F.inter,
          fontSize: 30,
          fontWeight: 500,
          color: IDV.gray,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}
      >
        comenta aqui
      </div>
    </div>
  );
};

// ─── OV-12: End card EP01/09 (67–73s) ────────────────────────────────────────
const OV12_EndCard: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = FPS;
  const s = spr(frame, fps, SPRINGS.smooth);
  const ty = lerp(s, [0, 1], [30, 0]);
  const op = lerp(s, [0, 1], [0, 1]);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 120,
        left: 48,
        right: 48,
        transform: `translateY(${ty}px)`,
        opacity: op,
      }}
    >
      <div
        style={{
          background: "rgba(10,10,10,0.9)",
          borderRadius: 16,
          overflow: "hidden",
          border: `1px solid rgba(255,255,255,0.1)`,
          backdropFilter: "blur(12px)",
        }}
      >
        {/* Green top bar */}
        <div
          style={{
            height: 4,
            background: IDV.secondary,
            width: "100%",
          }}
        />
        <div style={{padding: "24px 32px"}}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: F.inter,
                  fontSize: 18,
                  fontWeight: 600,
                  color: IDV.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  marginBottom: 6,
                }}
              >
                EP 01 / 09
              </div>
              <div
                style={{
                  fontFamily: F.bebas,
                  fontSize: 40,
                  color: IDV.white,
                  letterSpacing: "0.06em",
                  lineHeight: 1.1,
                }}
              >
                MODO AUTOMÁTICO
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 4,
              }}
            >
              <div
                style={{
                  fontFamily: F.inter,
                  fontSize: 16,
                  fontWeight: 500,
                  color: IDV.gray,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                PRÓXIMO →
              </div>
              <div
                style={{
                  fontFamily: F.inter,
                  fontSize: 20,
                  fontWeight: 700,
                  color: IDV.third,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                O CMO
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPOSITION
// ═══════════════════════════════════════════════════════════════════════════════
export const ModoAutomaticoEP01v3: React.FC = () => {
  const fps = FPS;

  return (
    <AbsoluteFill style={{background: "#000"}}>
      {/* A-ROLL */}
      <Video
        src={staticFile("aroll/ep01_aroll_v2.mp4")}
        style={{width: "100%", height: "100%", objectFit: "cover"}}
      />

      {/* Cinematic texture */}
      <FilmGrain opacity={0.03} />
      <Vignette />

      {/* PERMANENT disclosure stamp */}
      <StampDisclosure />

      {/* ── OV-01: 3:00h timer (7–10s) */}
      <Sequence from={7 * fps} durationInFrames={3 * fps}>
        <OV01_Timer />
      </Sequence>

      {/* ── OV-02: Feed pobre grid (13–15.5s) */}
      <Sequence from={13 * fps} durationInFrames={Math.round(2.5 * fps)}>
        <OV02_FeedPobre />
      </Sequence>

      {/* ── OV-03: 3 estilos pills (17–20s) */}
      <Sequence from={17 * fps} durationInFrames={3 * fps}>
        <OV03_EstilosPills />
      </Sequence>

      {/* ── OV-04: FLARE hero card (24–29s) */}
      <Sequence from={24 * fps} durationInFrames={5 * fps}>
        <OV04_FlareCard />
      </Sequence>

      {/* ── OV-05: 3 teams pills (30–35s) */}
      <Sequence from={30 * fps} durationInFrames={5 * fps}>
        <OV05_TeamsStrip />
      </Sequence>

      {/* ── OV-06: TIME DESIGN lower-third (35–41s, covers jump cut @ 37.3s) */}
      <Sequence from={35 * fps} durationInFrames={6 * fps}>
        <LowerThird
          name="TIME DESIGN"
          title="cuida do estático"
          accentColor={IDV.secondary}
          position="bottomLeft"
          enterDuration={20}
          holdDuration={260}
          exitDuration={20}
        />
      </Sequence>

      {/* ── OV-07: FLARE arbita stamp top-right (37–44s) */}
      <Sequence from={37 * fps} durationInFrames={7 * fps}>
        <OV07_FlareStamp />
      </Sequence>

      {/* ── OV-08: 3h → 20min hero stat (47–52s) */}
      <Sequence from={47 * fps} durationInFrames={5 * fps}>
        <OV08_StatHero />
      </Sequence>

      {/* ── OV-09: MESMA MARCA badge (52–56s) */}
      <Sequence from={52 * fps} durationInFrames={4 * fps}>
        <OV09_MesmaMarca />
      </Sequence>

      {/* ── OV-10: 100% AI ribbon (57–62s) */}
      <Sequence from={57 * fps} durationInFrames={5 * fps}>
        <OV10_AiRibbon />
      </Sequence>

      {/* ── OV-11: TIME CRIATIVO pulse CTA (62–67s) */}
      <Sequence from={62 * fps} durationInFrames={5 * fps}>
        <OV11_CTAPulse />
      </Sequence>

      {/* ── OV-12: End card EP01/09 (67–73s) */}
      <Sequence from={67 * fps} durationInFrames={6 * fps}>
        <OV12_EndCard />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
